import { useCallback, useState } from "react";
import type { FieldValues, Path, UseFormClearErrors, UseFormRegister, UseFormSetError } from "react-hook-form";
import { useDispatch } from "react-redux";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import type { AdmissionTaskPrefillPayload } from "@/src/hooks/useAdmissionTaskPrefill";
import { fetchAdmissionPrefillPayloadByTaskId } from "@/src/utils/tasks/fetchAdmissionPrefillPayloadByTaskId";

interface AOPTaskIdInputProps<TForm extends FieldValues> {
  register: UseFormRegister<TForm>;
  setError: UseFormSetError<TForm>;
  clearErrors: UseFormClearErrors<TForm>;
  applyTaskPrefill: (payload: AdmissionTaskPrefillPayload) => void;
  lastPrefilledTaskIdRef: React.MutableRefObject<string>;
  onCopy?: () => void;
  taskIdField?: Path<TForm>;
}

const AOPTaskIdInput = <TForm extends FieldValues>({
  register,
  setError,
  clearErrors,
  applyTaskPrefill,
  lastPrefilledTaskIdRef,
  onCopy,
  taskIdField = "task_id" as Path<TForm>,
}: AOPTaskIdInputProps<TForm>) => {
  const dispatch = useDispatch();
  const { notify } = usePushNotifications();
  const [isLoading, setIsLoading] = useState(false);
  const taskIdRegister = register(taskIdField);

  const loadTaskById = useCallback(
    async (rawTaskId: string) => {
      const normalizedId = rawTaskId.trim();
      if (!normalizedId || isLoading) return;
      if (normalizedId === lastPrefilledTaskIdRef.current) return;

      setIsLoading(true);
      clearErrors(taskIdField);
      dispatch(setIsPreloyder(true));

      try {
        const payload = await fetchAdmissionPrefillPayloadByTaskId(normalizedId);
        if (!payload) {
          setError(taskIdField, {
            type: "manual",
            message: "Task not found",
          });
          notify({
            title: "Task not found",
            message: `No task found with ID "${normalizedId}".`,
            type: "error",
          });
          return;
        }

        applyTaskPrefill(payload);
        notify({
          title: "Task loaded",
          message: "Form fields were prefilled from the task.",
          type: "success",
        });
      } catch (error) {
        console.error("[AdmissionOffice] Task ID lookup failed:", error);
        setError(taskIdField, {
          type: "manual",
          message: "Failed to load task",
        });
        notify({
          title: "Task lookup failed",
          message: "Could not load task data. Please try again.",
          type: "error",
        });
      } finally {
        setIsLoading(false);
        dispatch(setIsPreloyder(false));
      }
    },
    [
      applyTaskPrefill,
      clearErrors,
      dispatch,
      isLoading,
      lastPrefilledTaskIdRef,
      notify,
      setError,
      taskIdField,
    ],
  );

  return (
    <div className="form-group-fields-input">
      <div className="form-group-fields-input-label">
        <label htmlFor="task_id">Task Id</label>
        <div className="form-group-fields-input-label-line"></div>
      </div>
      <input
        className="task_id"
        id="task_id"
        type="text"
        disabled={isLoading}
        {...taskIdRegister}
        onBlur={(event) => {
          taskIdRegister.onBlur(event);
          void loadTaskById(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          void loadTaskById((event.target as HTMLInputElement).value);
        }}
      />
      <div className="form-group-fields-input-svg">
        <div className="form-group-fields-input-svg-line"></div>
        {onCopy ? (
          <div className="form-group-fields-input-svg-copy" onClick={onCopy}>
            <AODSvgExport.FullNameCopySvg />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AOPTaskIdInput;
